"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import { TurnstileService } from "../../../Service/TurnstileService"
import type { Turnstile } from "./types"
import Swal from "sweetalert2"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: Turnstile | null
}

const TurnstileModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, initialData }) => {
  const [form, setForm] = useState<Omit<Turnstile, "id">>({
    number: "",
    status: "active",
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    if (initialData) {
      setForm({
        number: initialData.number,
        status: initialData.status,
      })
    } else {
      setForm({
        number: "",
        status: "active",
      })
    }
    setErrors({})
  }, [initialData, isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setForm({
      ...form,
      [name]: value,
    })

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!form.number.trim()) {
      newErrors.number = "Turnstile number is required"
    }

    if (!form.status) {
      newErrors.status = "Status is required"
    }

    return newErrors
  }

  const handleSubmit = async () => {
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      setLoading(true)
      if (initialData) {
        await TurnstileService.update(initialData.id, form)
        await Swal.fire("Updated!", "Turnstile updated successfully.", "success")
      } else {
        await TurnstileService.create(form)
        await Swal.fire("Created!", "Turnstile created successfully.", "success")
      }
      onSuccess()
      toggle()
    } catch (error: any) {
      console.error("Failed to save turnstile:", error)
      if (error.response?.data?.number) {
        setErrors({ number: error.response.data.number[0] })
      } else {
        await Swal.fire("Error", "Something went wrong. Please try again.", "error")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>{initialData ? "Edit" : "Add"} Turnstile</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="number">Turnstile Number *</Label>
          <Input
            name="number"
            value={form.number}
            onChange={handleChange}
            invalid={!!errors.number}
            placeholder="Enter turnstile number"
          />
          <FormFeedback>{errors.number}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label for="status">Status *</Label>
          <Input type="select" name="status" value={form.status} onChange={handleChange} invalid={!!errors.status}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Under Maintenance</option>
          </Input>
          <FormFeedback>{errors.status}</FormFeedback>
        </FormGroup>

        <div className="text-end mt-4">
          <Button color="secondary" onClick={toggle} className="me-2">
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner size="sm" /> : initialData ? "Update" : "Create"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default TurnstileModal
